import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import { fetch_requestor_application_assigned_users } from '@/services/requestor_portal_service';
import {
  CREATE_USER,
  GET_ROLE_GROUPS,
  GET_USER_ROLES,
  GET_USERS,
  UPDATE_USER,
  get_request_config,
  normalize_user_role,
} from '@/utils/system_constants/constant.js';
import { fetch_open_company_cases } from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_live_queue';
import {
  read_session_current_role,
  read_session_project_id
} from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_common';
import { with_sla_runtime } from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_sla_helpers';
import {
  PANEL_FILTERS,
  build_panel_filters,
  build_metrics,
  build_regional_scopes,
  build_role_mix,
  filter_members,
  map_panel_member,
  panel_member_is_allowed,
  map_role,
  map_role_group,
  parse_api_data,
  response_items
} from './panel_register_helpers';
import {
  DEFAULT_SLA_BADGE_CLASS,
  SLA_BADGE_CLASSES,
} from './panel_register_constants';
import { group_filter_key } from './panel_register_common_helpers';
import {
  assert_success,
  create_empty_form,
  create_payload,
  panel_error_message,
  update_payload,
  validate_panel_form
} from './panel_register_form_helpers';

const PANEL_NAME_FIELDS = new Set(['first_name', 'last_name']);
const PANEL_NEUTRAL_SLA_BADGE_CLASS = 'bg-light text-secondary border';

function live_sla_badge_class(value = '') {
  const numeric = Number(String(value || '').replace('%', ''));
  if (!Number.isFinite(numeric)) {
    return PANEL_NEUTRAL_SLA_BADGE_CLASS;
  }

  const matched = SLA_BADGE_CLASSES.find((item) => numeric >= item.minimum);
  return matched?.class_name || DEFAULT_SLA_BADGE_CLASS;
}

function assignment_user_ids(assignments = []) {
  return (Array.isArray(assignments) ? assignments : [])
    .map((assignment) => Number(
      assignment?.assigned_to_id
      || assignment?.user_id
      || assignment?.assigned_to?.id
    ))
    .filter(Number.isFinite);
}

function normalized_sla_text(case_item = {}) {
  return [
    case_item?.sla,
    case_item?.sla_class,
    case_item?.sla_runtime?.status,
    case_item?.sla_runtime?.status_label,
    case_item?.sla_runtime?.badge_label,
    case_item?.due_status,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function case_has_sla_breach(case_item = {}) {
  const value = normalized_sla_text(case_item);
  return value.includes('overdue') || value.includes('breach') || value.includes('breached');
}

function case_has_sla_warning(case_item = {}) {
  const value = normalized_sla_text(case_item);
  return !case_has_sla_breach(case_item)
    && (
      value.includes('left')
      || value.includes('warning')
      || value.includes('at-risk')
      || value.includes('due')
    );
}

async function enrich_panel_case(case_item = {}) {
  let next_case = case_item;

  try {
    const assignments = await fetch_requestor_application_assigned_users(case_item.application_id);
    next_case = { ...next_case, application_assigned_users: assignments };
  } catch {
    next_case = { ...next_case, application_assigned_users: [] };
  }

  try {
    return await with_sla_runtime(next_case);
  } catch {
    return next_case;
  }
}

async function fetch_panel_live_cases(project_id = null) {
  const open_cases = await fetch_open_company_cases(project_id);
  return Promise.all(open_cases.map(enrich_panel_case));
}

function build_member_case_stats(cases = []) {
  return cases.reduce((stats, case_item) => {
    const user_ids = assignment_user_ids(case_item?.application_assigned_users);
    if (!user_ids.length) {
      return stats;
    }

    const is_breached = case_has_sla_breach(case_item);
    const is_warning = case_has_sla_warning(case_item);
    user_ids.forEach((user_id) => {
      const current = stats.get(user_id) || {
        open_count: 0,
        warning_count: 0,
        breached_count: 0,
      };
      current.open_count += 1;
      current.warning_count += is_warning ? 1 : 0;
      current.breached_count += is_breached ? 1 : 0;
      stats.set(user_id, current);
    });

    return stats;
  }, new Map());
}

function live_member_metrics(member_stats = null) {
  const open_count = Number(member_stats?.open_count || 0);
  const warning_count = Number(member_stats?.warning_count || 0);
  const breached_count = Number(member_stats?.breached_count || 0);
  const on_track_count = Math.max(open_count - warning_count - breached_count, 0);
  const sla_value = open_count ? `${Math.round((on_track_count / open_count) * 100)}%` : 'No SLA data';

  return {
    caseload: String(open_count),
    caseload_meta: open_count
      ? `${warning_count} warning · ${breached_count} breached`
      : 'No active caseload',
    sla: sla_value,
    sla_badge_class: live_sla_badge_class(sla_value),
  };
}

function apply_live_panel_metrics(members = [], cases = []) {
  const case_stats = build_member_case_stats(cases);
  return members.map((member) => ({
    ...member,
    ...live_member_metrics(case_stats.get(Number(member.id)) || null),
  }));
}

function find_created_user_id(response_data = {}) {
  const data = response_data?.data || {};
  return Number(
    response_data?.user_id
    || response_data?.id
    || data?.user_id
    || data?.id
    || data?.user?.id
  ) || null;
}

function build_created_panel_member_preview(form = {}, roles = [], role_groups = []) {
  const selected_role = roles.find((role) => String(role.role) === String(form.role)) || {};
  const selected_group = role_groups.find((group) => String(group.id) === String(form.role_group_id)) || {};
  const synthetic_id = find_positive_number(form.user_id) || Date.now();
  return map_panel_member({
    id: synthetic_id,
    user_id: synthetic_id,
    role_group_id: selected_group.id || '',
    role_group_name: selected_group.name || '',
    specialism: selected_role.role || form.role || '',
    user: {
      id: synthetic_id,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      date_joined: new Date().toISOString(),
      role_id: selected_role.id || '',
      role: {
        id: selected_role.id || '',
        role: selected_role.role || form.role || '',
        group_id: selected_group.id || '',
        group_name: selected_group.name || '',
        group: {
          id: selected_group.id || '',
          name: selected_group.name || '',
        }
      }
    }
  }, 0, roles, role_groups);
}

function upsert_member_by_email(members = [], next_member = {}) {
  const next_email = String(next_member.email || '').trim().toLowerCase();
  if (!next_email) {
    return [next_member, ...members];
  }

  const remaining = members.filter((member) =>
    String(member.email || '').trim().toLowerCase() !== next_email
  );
  return [next_member, ...remaining];
}

function find_positive_number(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

async function fetch_all_panel_users(current_role = '') {
  const users = [];
  let page = 1;
  let total_pages = 1;

  do {
    const response = await axios.get(
      GET_USERS,
      get_request_config({ params: { page, page_size: 100, current_role } })
    );
    const data = parse_api_data(response.data);
    users.push(...response_items(response.data));
    total_pages = Number(data?.pagination?.total_pages || 1);
    page += 1;
  } while (page <= total_pages);

  return users;
}

function create_panel_register_state() {
  return {
    active_filter: 'all',
    current_page: 1,
    page_size: 10,
    filters: PANEL_FILTERS,
    members: [],
    roles: [],
    role_groups: [],
    is_loading: false,
    roles_loading: false,
    creating_member: false,
    updating_member: false,
    create_form: create_empty_form(),
    edit_form: create_empty_form()
  };
}

export default {
  name: 'PanelRegister',
  components: {
    BlockingLoaderOverlay,
  },
  mixins: [alert_mixin],
  data: () => create_panel_register_state(),
  computed: {
    project_id() {
      return this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id();
    },
    current_role() {
      return this.$store?.getters?.currentRole || read_session_current_role();
    },
    filtered_members() {
      return filter_members(this.members, this.active_filter);
    },
    total_pages() {
      const pages = Math.ceil(this.filtered_members.length / this.page_size);
      return Math.max(1, pages);
    },
    paginated_members() {
      const current_page = Math.min(this.current_page, this.total_pages);
      const start = (current_page - 1) * this.page_size;
      const end = start + this.page_size;
      return this.filtered_members.slice(start, end);
    },
    pagination_label() {
      const total = this.filtered_members.length;
      if (!total) return '0 of 0 panel members';

      const current_page = Math.min(this.current_page, this.total_pages);
      const start = (current_page - 1) * this.page_size + 1;
      const end = Math.min(current_page * this.page_size, total);
      return `${start} to ${end} of ${total} panel members`;
    },
    metrics() {
      return build_metrics(this.members);
    },
    regional_scopes() {
      return build_regional_scopes(this.members, this.role_groups);
    },
    role_mix() {
      return build_role_mix(this.members);
    },
    create_form_is_system_admin() {
      return role_is_system_admin(this.create_form.role);
    },
    edit_form_is_system_admin() {
      const selected_role = this.roles.find((role) => String(role.id) === String(this.edit_form.role_id));
      return role_is_system_admin(selected_role?.role || this.edit_form.role);
    },
    page_loading() {
      return this.is_loading || this.roles_loading;
    },
    page_loading_message() {
      return 'Loading panel register...';
    },
    page_loading_detail() {
      return 'Fetching panel members, roles, and role groups.';
    }
  },
  mounted() {
    this.fetch_panel_register_data();
  },
  methods: {
    set_filter(filter_key) {
      this.active_filter = filter_key;
      this.current_page = 1;
    },

    set_page(page) {
      const next_page = Math.min(Math.max(1, page), this.total_pages);
      this.current_page = next_page;
    },

    reset_create_form() {
      this.create_form = create_empty_form(
        this.roles[0]?.role || '',
        this.role_groups[0]?.id ? String(this.role_groups[0].id) : ''
      );
    },

    open_create_panel_member_modal() {
      if (!this.roles.length) {
        this.fetch_roles();
      }
      if (!this.role_groups.length) {
        this.fetch_role_groups();
      }

      this.reset_create_form();
    },

    open_edit_panel_member_modal(member) {
      this.edit_form = {
        ...create_empty_form(),
        user_id: member.user_id || member.id,
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        email: member.email || '',
        original_email: member.email || '',
        phone_number: member.phone_number || '',
        role: member.role || '',
        role_id: member.role_id || '',
        role_group_id: this.resolve_member_group_id(member)
      };

      if (!this.roles.length) {
        this.fetch_roles();
      }
      if (!this.role_groups.length) {
        this.fetch_role_groups().then(() => {
          if (
            String(this.edit_form.user_id || '') === String(member.user_id || member.id || '')
            && !this.edit_form.role_group_id
          ) {
            this.edit_form.role_group_id = this.resolve_member_group_id(member);
          }
        });
      }
    },

    resolve_member_group_id(member = {}) {
      if (member.group_id) {
        return String(member.group_id);
      }

      const member_group_keys = [
        member.group_key,
        member.region_key,
        member.team_key,
        group_filter_key(member.scope),
      ].filter(Boolean);

      const matched_group = this.role_groups.find((group) =>
        member_group_keys.includes(group_filter_key(group.name))
      );

      return matched_group?.id ? String(matched_group.id) : '';
    },

    sanitize_panel_name_field(form_name, field_name) {
      const form = this.panel_form(form_name);
      if (!form || !PANEL_NAME_FIELDS.has(field_name)) return;

      const current_value = Object.entries(form).find(([key]) => key === field_name)?.[1];
      this.apply_panel_name_field(form, field_name, clean_panel_name(current_value));
    },

    sanitize_panel_phone_field(form_name) {
      const form = this.panel_form(form_name);
      if (!form) return;

      form.phone_number = String(form.phone_number || '')
        .replace(/\D/g, '')
        .slice(0, 10);
    },

    handle_create_role_change() {
      if (this.create_form_is_system_admin) {
        this.create_form.role_group_id = '';
      } else if (!this.create_form.role_group_id && this.role_groups[0]?.id) {
        this.create_form.role_group_id = String(this.role_groups[0].id);
      }
    },

    handle_edit_role_change() {
      if (this.edit_form_is_system_admin) {
        this.edit_form.role_group_id = '';
      } else if (!this.edit_form.role_group_id && this.role_groups[0]?.id) {
        this.edit_form.role_group_id = String(this.role_groups[0].id);
      }
    },

    async fetch_roles() {
      this.roles_loading = true;

      try {
        const response = await axios.get(GET_USER_ROLES, get_request_config());
        this.roles = response_items(response.data).map(map_role).filter((role) => role.role);
        this.refresh_panel_filters();

        if (!this.create_form.role && this.roles.length) {
          this.create_form.role = this.roles[0].role;
        }
      } catch {
        this.roles = [];
      } finally {
        this.roles_loading = false;
      }
    },

    async fetch_role_groups() {
      const response = await axios.get(GET_ROLE_GROUPS, get_request_config()).catch(() => null);
      this.role_groups = response
        ? response_items(response.data).map(map_role_group).filter((group) => group.id && group.name)
        : [];
      if (!this.create_form.role_group_id && this.role_groups.length) {
        this.create_form.role_group_id = String(this.role_groups[0].id);
      }
      this.refresh_panel_filters();
    },

    async fetch_panel_register_data() {
      this.is_loading = true;
      this.roles_loading = true;

      try {
        await Promise.all([
          this.fetch_role_groups(),
          this.fetch_roles()
        ]);
        await this.fetch_panel_members();
      } finally {
        this.roles_loading = false;
      }
    },

    refresh_panel_filters() {
      this.filters = build_panel_filters(this.role_groups);

      if (!this.filters.some((filter) => filter.key === this.active_filter)) {
        this.active_filter = 'all';
      }
    },

    async fetch_panel_members() {
      this.is_loading = true;

      try {
        const all_users = await fetch_all_panel_users(this.current_role);
        const mapped_members = all_users
          .map((profile, index) => map_panel_member(profile, index, this.roles, this.role_groups))
          .filter((member) => member.id != null)
          .filter(panel_member_is_allowed);
        const live_cases = await fetch_panel_live_cases(this.project_id);
        this.members = apply_live_panel_metrics(mapped_members, live_cases);
      } catch (error) {
        this.members = [];
        this.show_error(panel_error_message(error, 'Failed to load panel members.'));
      } finally {
        this.is_loading = false;
      }
    },

    async create_panel_member() {
      const validation_message = validate_panel_form(this.create_form);
      if (validation_message) {
        this.show_error(validation_message);
        return;
      }

      this.creating_member = true;
      const loader = await this.show_loader('Creating panel member...');

      try {
        const submitted_form = {
          ...this.create_form,
          project_id: this.project_id
        };
        const response = await axios.post(
          CREATE_USER,
          create_payload(submitted_form),
          get_request_config()
        );
        const response_data = assert_success(response.data, 'Panel member creation failed.');

        this.close_modal('create_panel_member_modal');
        this.reset_create_form();
        this.active_filter = 'all';
        this.current_page = 1;
        await this.fetch_panel_members();
        const created_email = String(submitted_form.email || '').trim().toLowerCase();
        const member_exists = this.members.some((member) =>
          String(member.email || '').trim().toLowerCase() === created_email
        );
        if (!member_exists) {
          const preview_member = build_created_panel_member_preview({
            ...submitted_form,
            user_id: find_created_user_id(response_data)
          }, this.roles, this.role_groups);
          this.members = upsert_member_by_email(this.members, preview_member);
        }
        this.show_success(response_data.message || 'Panel member created successfully.');
      } catch (error) {
        this.show_error(panel_error_message(error, 'Failed to create panel member.'));
      } finally {
        this.creating_member = false;
        this.close_loader(loader);
      }
    },

    async update_panel_member() {
      const submitted_form = {
        ...this.edit_form,
        project_id: this.project_id,
        is_system_admin: this.edit_form_is_system_admin
      };
      const validation_message = validate_panel_form(submitted_form, true);
      if (validation_message) {
        this.show_error(validation_message);
        return;
      }

      this.updating_member = true;
      const loader = await this.show_loader('Updating panel member...');

      try {
        const response = await axios.post(
          UPDATE_USER,
          update_payload(submitted_form),
          get_request_config()
        );
        const response_data = assert_success(response.data, 'Panel member update failed.');

        this.close_modal('edit_panel_member_modal');
        this.active_filter = 'all';
        await this.fetch_panel_members();
        this.show_success(response_data.message || 'Panel member updated successfully.');
      } catch (error) {
        this.show_error(panel_error_message(error, 'Failed to update panel member.'));
      } finally {
        this.updating_member = false;
        this.close_loader(loader);
      }
    },

    panel_form(form_name) {
      if (form_name === 'create_form') {
        return this.create_form;
      }

      return form_name === 'edit_form' ? this.edit_form : null;
    },

    apply_panel_name_field(form, field_name, value) {
      if (field_name === 'first_name') {
        form.first_name = value;
        return;
      }

      if (field_name === 'last_name') {
        form.last_name = value;
      }
    }
  }
};

function clean_panel_name(value = '') {
  return String(value || '')
    .replace(/[^A-Za-z\s'-]/g, '')
    .slice(0, 50);
}

function role_is_system_admin(role = '') {
  return normalize_user_role(role) === 'System Admin';
}

